import { useCallback, useState } from 'react'

import {
  EventRegistration,
  QueryEventRegistrationsArgs,
} from '@tribeplatform/gql-client/types'
import type { EventRegistrationFields } from '@tribeplatform/gql-client/types/generated'

import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'

import { getEventRegistrationsKey } from './event.key.js'

export function useExportEventRegistrations(options: {
  variables: Pick<QueryEventRegistrationsArgs, 'eventId'>
  fields?: EventRegistrationFields
  onError?: () => void
  onSuccess?: (items: EventRegistration[]) => void
}) {
  const queryClient = useQueryClient()
  const eventsListKey = getEventRegistrationsKey({
    variables: options.variables,
    fields: options.fields || 'all',
  })
  const { client } = useTribeClient()
  const [isLoading, setIsLoading] = useState(false)

  const exportCSV = useCallback(async () => {
    try {
      if (isLoading) {
        return
      }
      setIsLoading(true)
      let after: string | undefined
      let hasNextPage = true
      const allRegistrations: EventRegistration[] = []

      while (hasNextPage) {
        // eslint-disable-next-line no-await-in-loop, no-loop-func
        const result = await queryClient.fetchQuery(eventsListKey, async () => {
          return client.events.eventRegistrations(
            { ...options.variables, after, limit: 20 },
            options.fields || {
              member: 'basic',
            },
          )
        })
        allRegistrations.push(...result.nodes)
        after = result.pageInfo?.endCursor
        hasNextPage = !!result.pageInfo?.hasNextPage
      }

      options.onSuccess?.(allRegistrations)
      setIsLoading(false)
    } catch {
      setIsLoading(false)
      options.onError?.()
    }
  }, [isLoading, queryClient, eventsListKey, client.events, options])

  return { exportCSV, isLoading }
}
